/**
 * Main Application Component
 * 
 * Handles routing between the three main views:
 * 1. Guest View (/) - Upload/take photo to search the gallery
 * 2. Photographer View (/photographer) - Upload event photos to S3
 * 3. Admin View (/admin) - Manage photographers, buckets, and indexing
 */
import React, { useState, useEffect } from "react";
import CameraCapture from "./components/CameraCapture";
import GalleryResults from "./components/GalleryResults";
import IndexStatus from "./components/IndexStatus";
import AdminLogin from "./components/AdminLogin";
import PhotographerLogin from "./components/PhotographerLogin";
import UploadPage from "./components/UploadPage";
import "./App.css";

function App() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  
  const checkSession = (key) => {
    const loginTime = localStorage.getItem(`${key}Time`);
    const isLoggedIn = localStorage.getItem(key) === "true";
    if (isLoggedIn && loginTime) {
      // 2 hours expiration
      if (Date.now() - parseInt(loginTime) > 2 * 60 * 60 * 1000) {
        localStorage.removeItem(key);
        localStorage.removeItem(`${key}Time`);
        return false;
      }
      return true;
    }
    return false;
  };

  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(() => {
    return checkSession("adminLoggedIn");
  });
  
  const [isPhotographerAuthenticated, setIsPhotographerAuthenticated] = useState(() => {
    return checkSession("photographerLoggedIn");
  });
  
  const [activeBucket, setActiveBucket] = useState(() => {
    return localStorage.getItem("activeBucket") || null;
  });
  const [showCamera, setShowCamera] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromBucket = params.get("from");
    if (fromBucket) {
      localStorage.setItem("activeBucket", fromBucket);
      setActiveBucket(fromBucket);
      // Clean up URL to just the base path without query string
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const handleLocationChange = () => setCurrentPath(window.location.pathname);
    window.addEventListener("popstate", handleLocationChange);
    return () => window.removeEventListener("popstate", handleLocationChange);
  }, []);

  const handleSearch = async (imageBlobs, previewUrls) => {
    setLoading(true);
    setError(null);
    setResults(null);
    setCapturedPhoto(previewUrls[0]);

    const formData = new FormData();
    imageBlobs.forEach((blob, i) => {
      formData.append("files", blob, `photo_${i}.jpg`);
    });

    try {
      const apiUrl = activeBucket ? `/api/search?bucket=${encodeURIComponent(activeBucket)}` : "/api/search";
      const res = await fetch(apiUrl, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Search failed");
      }

      if (!data.success) {
        setError(data.error || "Search failed");
      } else {
        setResults(data);
      }
    } catch (err) {
      setError(err.message || "Network error. Is the backend running?");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResults(null);
    setError(null);
    setCapturedPhoto(null);
    setShowCamera(false);
  };

  const isAdminRoute = currentPath === "/admin";
  const isPhotographerRoute = currentPath === "/photographer";

  const handleLogout = () => {
    if (isAdminRoute) {
      localStorage.removeItem("adminLoggedIn");
      localStorage.removeItem("adminLoggedInTime");
      setIsAdminAuthenticated(false);
    } else if (isPhotographerRoute) {
      localStorage.removeItem("photographerLoggedIn");
      localStorage.removeItem("photographerLoggedInTime");
      setIsPhotographerAuthenticated(false);
    }
  };

  const showLogoutButton = (isAdminRoute && isAdminAuthenticated) || (isPhotographerRoute && isPhotographerAuthenticated);

  return (
    <div className="flex flex-col min-h-screen">
      <header className="glass-header">
        <div className="max-w-[1100px] mx-auto px-6 h-[70px] flex items-center justify-between">
          <div className="flex items-center gap-3 font-extrabold text-2xl tracking-tight cursor-pointer bg-accent-gradient bg-clip-text text-transparent transition-opacity hover:opacity-80" onClick={() => window.location.href = "/"}>
            <span className="text-3xl text-gray-100" style={{ WebkitTextFillColor: 'initial' }}>👤</span>
            <span>CloudPlayXP Face Search</span>
          </div>
          {showLogoutButton && (
            <button 
              onClick={handleLogout}
              className="text-sm font-medium text-textMuted border border-borderBase px-4 py-2 rounded-lg hover:bg-white/5 hover:text-white transition-all"
            >
              Log Out
            </button>
          )}
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto w-full flex-1 flex flex-col px-6 py-12">
        {!isAdminRoute && !isPhotographerRoute && (
          <div className="animate-fadeIn flex-1 flex flex-col justify-center">
            {!results && !loading && (
              <>
                <div className="text-center mb-12">
                  <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4 bg-gradient-to-br from-white to-gray-400 bg-clip-text text-transparent">Find yourself in the gallery</h1>
                  <p className="text-textMuted text-lg max-w-[600px] mx-auto">Take a photo or upload one — we'll find every image that includes your face.</p>
                </div>
                {!activeBucket ? (
                  <div className="text-center mt-8 bg-[rgba(255,51,102,0.1)] border border-[rgba(255,51,102,0.3)] rounded-2xl p-8 max-w-lg mx-auto">
                    <div className="text-4xl mb-3">📸</div>
                    <h2 className="text-2xl font-bold text-white mb-2">Event Not Selected</h2>
                    <p className="text-textMuted">Please scan your specific event QR code or link to access the gallery.</p>
                  </div>
                ) : !showCamera ? (
                  <div className="text-center mt-8">
                    <button 
                      className="bg-accent-gradient text-white px-10 py-4 rounded-full font-bold text-lg shadow-glow animate-pulseGlow hover:-translate-y-1 hover:shadow-glow-strong transition-all active:translate-y-px"
                      onClick={() => setShowCamera(true)}
                    >
                      Start
                    </button>
                    <p className="mt-4 text-sm text-textMuted">Searching in event: <span className="text-accentSolid font-semibold">{activeBucket}</span></p>
                  </div>
                ) : (
                  <CameraCapture onCapture={handleSearch} />
                )}
              </>
            )}

            {loading && (
              <div className="text-center py-20 px-5">
                <div className="w-14 h-14 border-4 border-borderBase border-t-accentSolid rounded-full animate-spinSlow mx-auto mb-6" />
                <p className="text-lg">Analyzing face and searching {capturedPhoto ? "gallery" : ""}…</p>
                {capturedPhoto && (
                  <img src={capturedPhoto} alt="Captured" className="w-32 h-32 object-cover rounded-full border-4 border-accentSolid mt-6 shadow-glow animate-pulseGlow mx-auto" />
                )}
              </div>
            )}

            {error && (
              <div className="text-center py-16 px-5 bg-bg2 rounded-2xl border border-[rgba(255,51,102,0.2)] max-w-lg mx-auto w-full">
                <div className="text-5xl mb-4">⚠️</div>
                <h3 className="mb-3 text-2xl font-bold">Oops!</h3>
                <p className="text-textMuted mb-6 text-lg">{error}</p>
                <button 
                  className="bg-accent-gradient text-white px-7 py-3 rounded-xl font-semibold shadow-glow hover:-translate-y-1 hover:shadow-glow-strong transition-all active:translate-y-px" 
                  onClick={handleReset}
                >
                  Try Again
                </button>
              </div>
            )}

            {results && (
              <GalleryResults
                results={results}
                capturedPhoto={capturedPhoto}
                onReset={handleReset}
              />
            )}
          </div>
        )}

        {isAdminRoute && (
          <div className="animate-fadeIn w-full flex-1 flex flex-col items-center justify-center">
            {isAdminAuthenticated ? (
              <IndexStatus />
            ) : (
              <div className="w-full flex justify-center mt-12">
                <AdminLogin onLogin={() => {
                  localStorage.setItem("adminLoggedIn", "true");
                  localStorage.setItem("adminLoggedInTime", Date.now().toString());
                  setIsAdminAuthenticated(true);
                }} />
              </div>
            )}
          </div>
        )}

        {isPhotographerRoute && (
          <div className="animate-fadeIn w-full flex-1 flex flex-col items-center justify-center">
            {isPhotographerAuthenticated ? (
              <UploadPage />
            ) : (
              <div className="w-full flex justify-center mt-12">
                <PhotographerLogin onLogin={() => {
                  localStorage.setItem("photographerLoggedIn", "true");
                  localStorage.setItem("photographerLoggedInTime", Date.now().toString());
                  setIsPhotographerAuthenticated(true);
                }} />
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
